import { useMemo } from "react";
import { GridColDef } from "@mui/x-data-grid";
import { LocalShipping } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CustomDataGrid from "../../../components/ui/customDataGrid/customDataGrid.component";
import EntityReferenceLink from "../../../components/ui/entityReference/entityReferenceLink.component";
import { StatusChip } from "../../../components/ui/statusChip/statusChip.component";
import {
  containsStringFilterOperators,
  equalsNumberFilterOperators,
  exactDateFilterOperators,
  singleSelectFilterOperators,
} from "../../../components/ui/customDataGrid/filterOperators";
import { SHIPMENT_STATUS_COLOR_MAP, SHIPMENT_STATUS_OPTIONS, SHIPMENTS_ENDPOINT } from "../../../consts";
import { useHasPermission } from "../../../contexts/auth";
import { formatDateTimeBadge, formatMoney } from "../../../utils";

export default function ShipmentsList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canViewShipments = useHasPermission("shipping.view_shipment");
  const canViewClients = useHasPermission("settlement.view_client");

  const statusOptions = useMemo(
    () => SHIPMENT_STATUS_OPTIONS.map((option) => ({ value: option.value, label: t(option.labelKey) })),
    [t]
  );

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      filterOperators: equalsNumberFilterOperators(),
    },
    {
      field: "status",
      headerName: t("Status"),
      minWidth: 120,
      type: "singleSelect",
      valueOptions: statusOptions,
      filterOperators: singleSelectFilterOperators(),
      renderCell: (params) => (
        <StatusChip label={params.row.status_display} value={params.row.status} colorMap={SHIPMENT_STATUS_COLOR_MAP} />
      ),
    },
    {
      field: "waybill",
      headerName: t("Waybill number"),
      minWidth: 150,
      filterOperators: containsStringFilterOperators(),
      valueGetter: (value) => value || "—",
    },
    {
      field: "receiver",
      headerName: t("Receiver"),
      minWidth: 200,
      flex: 1,
      sortable: false,
      filterable: false,
      valueGetter: (value, row) =>
        [row.receiver_company || row.receiver_name, row.receiver_city].filter(Boolean).join(", "),
    },
    {
      field: "client",
      headerName: t("Client"),
      minWidth: 160,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <EntityReferenceLink
          entityType="client"
          entityId={params.row.client_info?.id}
          label={params.row.client_info?.name}
          canPreview={canViewClients}
        />
      ),
    },
    {
      field: "parts_summary",
      headerName: t("Parts"),
      minWidth: 200,
      flex: 1,
      sortable: false,
      filterable: false,
    },
    {
      field: "service_name",
      headerName: t("Carrier"),
      minWidth: 140,
      sortable: false,
      filterable: false,
    },
    {
      field: "price_gross",
      headerName: t("Price (gross)"),
      type: "number",
      filterable: false,
      valueFormatter: (value) => (value ? formatMoney(value) : "—"),
    },
    {
      field: "created_at",
      headerName: t("Created at"),
      filterOperators: exactDateFilterOperators(),
      renderCell: (params) => formatDateTimeBadge(params.value) || "-",
    },
  ];

  if (!canViewShipments) {
    return null;
  }

  return (
    <CustomDataGrid
      icon={<LocalShipping />}
      title={t("Shipments")}
      url={SHIPMENTS_ENDPOINT}
      gridSettingsKey="SHIPMENTS"
      columns={columns}
      refreshEventName={["shipmentAdded", "shipmentUpdated"]}
      onRowClick={(id: string) => navigate(`/service/shipments/${id}`)}
    />
  );
}
